/*
 * LUEYA — lueya-order-to-cart.js
 * Two ways to add a product, both through the native Horizon product form:
 *
 * - "Commander" (product cards) / "Commander maintenant" (product page, size
 *   chooser): once the add has succeeded, go straight to /cart — where the
 *   EasySell COD button takes over (EasySell only intercepts the cart page,
 *   never a direct /checkout).
 * - "Ajouter au panier" ([data-lueya-add-to-cart]: icon button on cards,
 *   secondary button on the product page; [data-lueya-quick-cart]: icon button
 *   of the desktop hover quick-add): the shopper stays on the page and the cart
 *   drawer opens. We only switch on the drawer's native `auto-open` attribute
 *   for that add (theme setting auto_open_cart_drawer stays off), so Horizon's
 *   cart-drawer.js opens it — and waits for the size chooser to close first.
 *   The drawer's "Commander" is a plain link to /cart (snippets/cart-summary.liquid).
 *
 * Hooks the standard `shopify:cart:lines-update` event dispatched by
 * <product-form-component> (assets/product-form.js) and waits for its promise,
 * so errors (sold out, max quantity…) keep Horizon's inline message instead.
 * The bundle composer adds through /cart/add.js directly and is not concerned.
 */
(function () {
  'use strict';

  var EVENT = 'shopify:cart:lines-update';
  var INTENT = 'lueyaIntent';
  var autoOpenTimer = null;
  var programmaticClick = false;

  function cartUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart';
  }

  function cartDrawer() {
    return document.querySelector('cart-drawer-component');
  }

  function setDrawerAutoOpen(on) {
    var drawer = cartDrawer();
    if (autoOpenTimer) clearTimeout(autoOpenTimer);
    autoOpenTimer = null;
    if (!drawer) return;
    if (on) {
      drawer.setAttribute('auto-open', '');
      // Safety net: never leave the drawer in auto-open mode if no add follows.
      autoOpenTimer = setTimeout(function () {
        drawer.removeAttribute('auto-open');
      }, 15000);
    } else {
      drawer.removeAttribute('auto-open');
    }
  }

  function markDrawerIntent(form) {
    if (!form || !cartDrawer()) return false;
    form.dataset[INTENT] = 'drawer';
    setDrawerAutoOpen(true);
    return true;
  }

  function clearDrawerIntent(form) {
    if (form) delete form.dataset[INTENT];
    setDrawerAutoOpen(false);
  }

  // Product cards with fixed buttons (home, collections, search): for a product
  // with several variants (bagues, Bracelet Signature), "Commander" and the cart+
  // icon must not add the first variant blindly — they open Horizon's quick-add
  // chooser instead (size/colour, then "Commander maintenant" → cart or "Ajouter
  // au panier" → drawer). Horizon renders a "choose" quick-add button in those
  // cards (hidden by CSS on cards that have fixed buttons); a card with a single
  // variant only gets the "add" one, so it keeps adding directly.
  document.addEventListener(
    'click',
    function (event) {
      if (!(event.target instanceof Element)) return;

      // Desktop hover quick-add: cart+ icon next to "Commander".
      var quickCart = event.target.closest('[data-lueya-quick-cart]');
      if (quickCart) {
        event.preventDefault();
        var quickAdd = quickCart.closest('quick-add-component');
        if (!quickAdd) return;
        var mode = quickAdd.getAttribute('data-quick-add-button');
        var target = quickAdd.querySelector(
          mode === 'add' ? '.quick-add__button--add' : '.quick-add__button--choose'
        );
        if (!target || target.disabled) return;
        if (mode === 'add') markDrawerIntent(target.closest('product-form-component'));
        programmaticClick = true;
        try {
          target.click();
        } finally {
          programmaticClick = false;
        }
        return;
      }

      var button = event.target.closest(
        '[id^="BuyButtons-ProductSubmitButton"], [data-lueya-add-to-cart], .quick-add__button--add'
      );
      if (!button) return;
      var form = button.closest('product-form-component');
      var isDrawerButton = button.hasAttribute('data-lueya-add-to-cart');

      var card = button.closest('product-card');
      var choose = card && !button.classList.contains('quick-add__button--add')
        ? card.querySelector('.quick-add__button--choose')
        : null;
      if (choose) {
        event.preventDefault();
        event.stopImmediatePropagation();
        clearDrawerIntent(form);
        choose.click();
        return;
      }

      if (programmaticClick) return;
      if (button.disabled) return;
      if (isDrawerButton) {
        markDrawerIntent(form);
      } else {
        clearDrawerIntent(form);
      }
    },
    true
  );

  document.addEventListener(EVENT, function (event) {
    if (event.action !== 'add') return;
    var source = event.target;
    if (!(source instanceof Element) || source.tagName.toLowerCase() !== 'product-form-component') return;

    var toDrawer = source.dataset[INTENT] === 'drawer';
    delete source.dataset[INTENT];
    var promise = event.promise && typeof event.promise.then === 'function' ? event.promise : null;

    if (toDrawer) {
      // cart-drawer.js has already read `auto-open` synchronously for this event.
      var settle = function () {
        setDrawerAutoOpen(false);
      };
      if (promise) promise.then(settle, settle);
      else settle();
      return;
    }

    if (/\/cart\/?$/.test(window.location.pathname)) return;
    if (!promise) return;

    promise
      .then(function (result) {
        if (result && result.detail && result.detail.didError) return;
        window.location.href = cartUrl();
      })
      .catch(function () {
        /* add failed or aborted: stay on the page, Horizon shows the error */
      });
  });

  // "Ajouter au panier" follows the state of the form's main button ("Commander" /
  // "Commander maintenant"): Horizon only updates that one (size chosen, variant
  // sold out, add in progress…).
  function syncSecondary(form) {
    var main = form.querySelector('[ref="addToCartButton"]');
    if (!main) return;
    var disabled = main.disabled;
    form.querySelectorAll('[data-lueya-add-to-cart]').forEach(function (btn) {
      if (btn.disabled !== disabled) btn.disabled = disabled;
    });
  }

  function watchForm(form) {
    if (form.__lueyaWatched || !form.querySelector('[data-lueya-add-to-cart]')) return;
    form.__lueyaWatched = true;
    syncSecondary(form);
    new MutationObserver(function () {
      syncSecondary(form);
    }).observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
  }

  function watchAll(root) {
    (root || document).querySelectorAll('product-form-component').forEach(watchForm);
  }

  // Forms appear later too (size chooser dialog, section re-renders).
  var scanQueued = false;
  new MutationObserver(function () {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(function () {
      scanQueued = false;
      watchAll(document);
    });
  }).observe(document.documentElement, { subtree: true, childList: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      watchAll(document);
    });
  } else {
    watchAll(document);
  }
})();
